<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { OnClickOutside } from '@vueuse/components';
import { Chrome } from '@lk77/vue3-color';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { getRandomColor, isHexColor } from 'dashboard/helper/labelColor';
import { useVuelidate } from '@vuelidate/core';

import NextButton from 'dashboard/components-next/button/Button.vue';

// digitt: DENG-7201 — a label marker can be a color or an emoji
const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

export default {
  components: {
    NextButton,
    EmojiIconPicker,
    OnClickOutside,
    Chrome,
  },
  props: {
    prefillTitle: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      color: '#000',
      description: '',
      title: '',
      showOnSidebar: true,
      showEmojiPicker: false,
      showColorPicker: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
    // digitt: DENG-7201 — current marker is an emoji when it isn't a hex color
    isEmojiColor() {
      return !isHexColor(this.color);
    },
  },
  mounted() {
    this.color = getRandomColor();
    this.title = this.prefillTitle.toLowerCase();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    // digitt: DENG-7201 — color and emoji are both stored in `color`; last choice wins
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    onPickEmoji(emoji) {
      this.color = emoji;
      this.showEmojiPicker = false;
    },
    toggleColorPicker() {
      this.showColorPicker = !this.showColorPicker;
    },
    onPickColor(value) {
      this.color = value.hex;
    },
    async addLabel() {
      try {
        await this.$store.dispatch('labels/create', {
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
        });
        useAlert(this.$t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('LABEL_MGMT.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t('LABEL_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        data-testid="label-title"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />

      <woot-input
        v-model="description"
        :class="{ error: v$.description.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        data-testid="label-description"
        @input="v$.description.$touch"
        @blur="v$.description.$touch"
      />

      <div class="w-full mb-4">
        <label>{{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}</label>
        <!-- digitt: DENG-7201 — choose a color OR an emoji (last choice wins) -->
        <div class="flex gap-2 items-center">
          <OnClickOutside class="relative" @trigger="showColorPicker = false">
            <NextButton
              faded
              slate
              sm
              icon="i-lucide-pipette"
              type="button"
              @click="toggleColorPicker"
            />
            <Chrome
              v-if="showColorPicker"
              disable-alpha
              :model-value="isEmojiColor ? '#1f93ff' : color"
              class="colorpicker--chrome"
              @update:model-value="onPickColor"
            />
          </OnClickOutside>
          <OnClickOutside class="relative" @trigger="showEmojiPicker = false">
            <NextButton
              faded
              slate
              sm
              icon="i-ph-smiley"
              type="button"
              @click="toggleEmojiPicker"
            />
            <EmojiIconPicker
              v-if="showEmojiPicker"
              mode="emoji"
              class="emoji-dialog left-[40px] top-[-180px]"
              @select="onPickEmoji($event.value)"
            />
          </OnClickOutside>
          <span
            class="flex items-center justify-center flex-shrink-0 w-8 h-8 border border-solid rounded-lg border-n-strong"
          >
            <span v-if="isEmojiColor" class="text-lg leading-none">
              {{ color }}
            </span>
            <span
              v-else
              class="rounded size-4"
              :style="{ background: color }"
            />
          </span>
        </div>
      </div>
      <div class="flex items-center w-full gap-2">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          data-testid="label-submit"
          :label="$t('LABEL_MGMT.FORM.CREATE')"
          :disabled="v$.title.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  :deep(input) {
    @apply lowercase;
  }
}

// digitt: DENG-7201 — position the color picker popover below its button
.colorpicker--chrome.vc-chrome {
  @apply shadow-lg bottom-full mb-2 absolute z-[9999] border border-solid border-n-weak rounded;

  ::v-deep {
    input {
      @apply bg-white dark:bg-white;
    }
  }
}

// digitt: DENG-7201 — hide EmojiInput's pointer arrow (doesn't align in this layout)
:deep(.emoji-dialog)::before {
  display: none;
}
</style>
